import { useMemo, useState, type ReactElement } from 'react';
import { CheckIcon, ChevronDownIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { useDisplayTimeZone } from '@/hooks/useDisplayTimeZone';
import {
  DEFAULT_TIME_ZONE,
  getBrowserTimeZone,
  setDisplayTimeZone,
  zoneLabel,
} from '@/lib/displayTimeZone';
import { supportedTimeZones } from '@kirin/api-client/helpers/time-zone';
import { ZoneExample } from './ZoneExample';

interface TimeZoneOption {
  value: string;
  label: string;
}

/** UTC, then the browser's zone, then every other IANA zone A→Z. */
function buildOptions(): TimeZoneOption[] {
  const now = new Date();
  const browser = getBrowserTimeZone();
  const zones = [DEFAULT_TIME_ZONE, browser, ...supportedTimeZones()];
  return [...new Set(zones)].map(tz => ({
    value: tz,
    label:
      tz === browser && tz !== DEFAULT_TIME_ZONE
        ? `${zoneLabel(tz, now)} · this browser`
        : zoneLabel(tz, now),
  }));
}

/**
 * Settings → Preferences → Time Zone. Picks the zone every date + time
 * rendered through `formatDateTime` uses. Saved per browser, like the
 * Timestamp Format setting.
 */
export function TimeZoneControl(): ReactElement {
  const zone = useDisplayTimeZone();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  // ~420 labels: build them once per mount.
  const baseOptions = useMemo(() => buildOptions(), []);
  // A saved zone the list lacks (an engine that spells it differently, or one
  // that lists no zones) goes last, so it stays selectable.
  const options = baseOptions.some(o => o.value === zone)
    ? baseOptions
    : [...baseOptions, { value: zone, label: zoneLabel(zone, new Date()) }];
  // Labels show `_` as a space, so "new_york" and "new york" both match.
  const q = query.trim().toLowerCase().replace(/_/g, ' ');
  const matches = q
    ? options.filter(o => o.label.toLowerCase().includes(q))
    : options;

  const onOpenChange = (next: boolean): void => {
    setOpen(next);
    setQuery('');
  };
  const pick = (tz: string): void => {
    setDisplayTimeZone(tz);
    onOpenChange(false);
  };

  return (
    <div className="flex flex-col gap-2">
      <Popover open={open} onOpenChange={onOpenChange}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-label="Time zone"
            className="w-full max-w-sm justify-between font-normal"
          >
            <span className="truncate" data-testid="tz-selected">
              {options.find(o => o.value === zone)?.label ?? zone}
            </span>
            <ChevronDownIcon className="opacity-50" />
          </Button>
        </PopoverTrigger>
        {/* A fixed height (capped by the space Radix finds), not one that
            follows the matches: a popover that shrank while typing flipped
            from above the trigger to below it mid-word. */}
        <PopoverContent
          className="flex h-80 w-(--radix-popover-trigger-width) flex-col p-0"
          align="start"
        >
          <div className="border-b p-2">
            <Input
              value={query}
              onChange={e => setQuery(e.target.value)}
              onKeyDown={e => {
                // Not on an empty query: its first match is UTC, so a bare
                // Enter would reset the saved zone.
                if (e.key === 'Enter' && q && matches[0]) {
                  pick(matches[0].value);
                }
              }}
              placeholder="Search time zones"
              aria-label="Search time zones"
            />
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto p-1">
            {matches.map(o => (
              <button
                key={o.value}
                type="button"
                data-option={o.value}
                aria-pressed={o.value === zone}
                onClick={() => pick(o.value)}
                className="flex w-full items-center justify-between gap-2 rounded-sm px-2 py-1.5 text-left text-sm outline-none hover:bg-accent focus-visible:bg-accent"
              >
                <span className="truncate">{o.label}</span>
                {o.value === zone && <CheckIcon className="size-4 shrink-0" />}
              </button>
            ))}
            {matches.length === 0 && (
              <p className="px-2 py-1.5 text-sm text-muted-foreground">
                No matching time zones.
              </p>
            )}
          </div>
        </PopoverContent>
      </Popover>
      <ZoneExample zone={zone} testId="tz-example" />
    </div>
  );
}
